/**
 * Combat. RULES section 6, implemented step by step.
 *
 * Nothing in this file branches on a card name or id.
 */

import { profileOf } from './cardDb';
import {
  emit,
  exhaustObject,
  damagePlayer,
  dealDamage,
  fireTriggers,
  type Ctx,
} from './mutate';
import {
  attackRequirements,
  cantBeBlockedByFilters,
  charactersOf,
  getEffectiveStats,
  matchesFilter,
  nameOf,
  opponentOf,
} from './query';
import { runStateBasedChecks } from './stateBasedChecks';
import type { BlockAssignment, GameState } from './types';
import { RulesError } from './types';

// ---------------------------------------------------------------------------
// Declaring attackers
// ---------------------------------------------------------------------------

/** Characters that could be declared as attackers right now (RULES 6.1). */
export function legalAttackers(state: GameState, playerId: string): string[] {
  const out: string[] = [];
  for (const c of charactersOf(state, playerId)) {
    if (c.exhausted) continue;
    const stats = getEffectiveStats(state, c.instanceId);
    if (stats.keywords.includes('staked')) continue;
    const continuous = c.controlSinceTurn < state.turn;
    if (!continuous && !stats.keywords.includes('ape')) continue;
    out.push(c.instanceId);
  }
  return out;
}

export function canSkipCombat(state: GameState, playerId: string): boolean {
  return legalAttackers(state, playerId).length === 0;
}

export function declareAttackers(ctx: Ctx, playerId: string, attackers: string[]): void {
  const state = ctx.state;
  const legal = legalAttackers(state, playerId);
  const unique = [...new Set(attackers)];
  for (const id of unique) {
    if (!legal.includes(id)) {
      throw new RulesError('CANT_ATTACK', `${nameOf(state, id)} cannot attack right now.`);
    }
  }
  // Characters an enemy static forces to attack must be included if able.
  const required = attackRequirements(state, playerId).filter((id) => legal.includes(id));
  for (const id of required) {
    if (!unique.includes(id)) {
      throw new RulesError('CANT_ATTACK', `${nameOf(state, id)} must attack this combat.`);
    }
  }

  state.combat.attackers = unique;
  for (const id of unique) {
    const o = state.objects[id];
    if (!o) continue;
    o.attacking = true;
    const stats = getEffectiveStats(state, id);
    if (!stats.keywords.includes('uptime')) exhaustObject(ctx, id);
  }
  emit(ctx, {
    type: 'AttackersDeclared',
    turn: state.turn,
    player: playerId,
    attackers: unique.map((id) => ({ instanceId: id, name: nameOf(state, id) })),
  });
  for (const id of unique) fireTriggers(ctx, 'self:attack', { sourceInstanceId: id });
}

// ---------------------------------------------------------------------------
// Declaring blockers
// ---------------------------------------------------------------------------

/** Can this specific character block this specific attacker? */
export function canBlock(state: GameState, blockerId: string, attackerId: string): { ok: boolean; reason?: string } {
  const blocker = state.objects[blockerId];
  const attacker = state.objects[attackerId];
  if (!blocker || !attacker) return { ok: false, reason: 'That character is not in combat.' };
  if (blocker.zone !== 'battlefield' || profileOf(blocker).type !== 'character') {
    return { ok: false, reason: 'Only characters on the battlefield can block.' };
  }
  if (blocker.exhausted) return { ok: false, reason: `${nameOf(state, blockerId)} is exhausted.` };
  if (!attacker.attacking) return { ok: false, reason: 'That character is not attacking.' };

  const bStats = getEffectiveStats(state, blockerId);
  const aStats = getEffectiveStats(state, attackerId);
  if (bStats.cantBlock) return { ok: false, reason: `${nameOf(state, blockerId)} cannot block.` };

  if (aStats.keywords.includes('offChain')) {
    if (!bStats.keywords.includes('offChain') && !bStats.keywords.includes('oracle')) {
      return {
        ok: false,
        reason: `${nameOf(state, attackerId)} is OFF-CHAIN and needs an OFF-CHAIN or ORACLE blocker.`,
      };
    }
  }

  for (const filter of cantBeBlockedByFilters(state, attackerId)) {
    if (matchesFilter(state, blocker, filter, 'any', attacker.controller, attackerId)) {
      return { ok: false, reason: `${nameOf(state, blockerId)} cannot block that character.` };
    }
  }

  return { ok: true };
}

/** Every legal blocker for one attacker, ignoring RAID's count requirement. */
export function legalBlockersFor(state: GameState, attackerId: string, defenderId: string): string[] {
  return charactersOf(state, defenderId)
    .filter((c) => canBlock(state, c.instanceId, attackerId).ok)
    .map((c) => c.instanceId);
}

export function validateBlocks(
  state: GameState,
  defenderId: string,
  assignments: BlockAssignment[],
): void {
  const seenBlockers = new Set<string>();
  const perAttacker = new Map<string, number>();
  for (const a of assignments) {
    const blocker = state.objects[a.blocker];
    if (!blocker || blocker.controller !== defenderId) {
      throw new RulesError('CANT_BLOCK', 'You do not control that blocker.');
    }
    if (seenBlockers.has(a.blocker)) {
      throw new RulesError('CANT_BLOCK', 'A character can block only one attacker.');
    }
    seenBlockers.add(a.blocker);
    if (!state.combat.attackers.includes(a.attacker)) {
      throw new RulesError('CANT_BLOCK', 'That character is not attacking.');
    }
    const check = canBlock(state, a.blocker, a.attacker);
    if (!check.ok) throw new RulesError('CANT_BLOCK', check.reason ?? 'Illegal block.');
    perAttacker.set(a.attacker, (perAttacker.get(a.attacker) ?? 0) + 1);
  }
  // RAID: zero blockers is fine, one is not.
  for (const attackerId of state.combat.attackers) {
    const n = perAttacker.get(attackerId) ?? 0;
    if (n === 1 && getEffectiveStats(state, attackerId).keywords.includes('raid')) {
      throw new RulesError(
        'CANT_BLOCK',
        `${nameOf(state, attackerId)} has RAID and must be blocked by two or more characters.`,
      );
    }
  }
}

/** Auto-order blockers most valuable first, which is also the bot default. */
export function autoOrderBlockers(state: GameState, blockerIds: string[]): string[] {
  return blockerIds.slice().sort((a, b) => {
    const sa = getEffectiveStats(state, a);
    const sb = getEffectiveStats(state, b);
    const va = sa.attack + sa.health;
    const vb = sb.attack + sb.health;
    if (va !== vb) return vb - va;
    return a < b ? -1 : 1;
  });
}

export function declareBlockers(ctx: Ctx, defenderId: string, assignments: BlockAssignment[]): void {
  const state = ctx.state;
  validateBlocks(state, defenderId, assignments);
  state.combat.blockers = {};
  for (const a of assignments) {
    const list = state.combat.blockers[a.attacker] ?? [];
    list.push(a.blocker);
    state.combat.blockers[a.attacker] = list;
    const o = state.objects[a.blocker];
    if (o) o.blocking = a.attacker;
  }
  for (const attackerId of Object.keys(state.combat.blockers)) {
    state.combat.blockers[attackerId] = autoOrderBlockers(state, state.combat.blockers[attackerId]);
  }
  emit(ctx, {
    type: 'BlockersDeclared',
    turn: state.turn,
    player: defenderId,
    blocks: assignments.map((a) => ({
      blocker: a.blocker,
      blockerName: nameOf(state, a.blocker),
      attacker: a.attacker,
      attackerName: nameOf(state, a.attacker),
    })),
  });
  for (const a of assignments) fireTriggers(ctx, 'self:block', { sourceInstanceId: a.blocker });
}

export function setBlockerOrder(ctx: Ctx, attackerId: string, order: string[]): void {
  const state = ctx.state;
  const current = state.combat.blockers[attackerId];
  if (!current) throw new RulesError('INVALID_ACTION', 'That attacker has no blockers to order.');
  const same =
    order.length === current.length && order.every((id) => current.includes(id)) &&
    new Set(order).size === order.length;
  if (!same) throw new RulesError('INVALID_ACTION', 'That order does not match the blockers.');
  state.combat.blockers[attackerId] = order.slice();
}

// ---------------------------------------------------------------------------
// Damage
// ---------------------------------------------------------------------------

interface DamageInstance {
  source: string;
  targetObject?: string;
  targetPlayer?: string;
  amount: number;
}

function damageOutput(state: GameState, instanceId: string): number {
  const stats = getEffectiveStats(state, instanceId);
  return Math.max(0, stats.attack + stats.damageBonus);
}

function participatesInStep(
  state: GameState,
  instanceId: string,
  step: 'frontrun' | 'regular',
): boolean {
  const hasFrontrun = getEffectiveStats(state, instanceId).keywords.includes('frontrun');
  return step === 'frontrun' ? hasFrontrun : !hasFrontrun;
}

/** True when the Frontrun step would do anything at all. */
export function frontrunRelevant(state: GameState): boolean {
  const ids = combatParticipants(state);
  return ids.some((id) => getEffectiveStats(state, id).keywords.includes('frontrun'));
}

export function combatParticipants(state: GameState): string[] {
  const out: string[] = [];
  for (const id of state.combat.attackers) {
    const o = state.objects[id];
    if (o && o.zone === 'battlefield') out.push(id);
  }
  for (const list of Object.values(state.combat.blockers)) {
    for (const id of list) {
      const o = state.objects[id];
      if (o && o.zone === 'battlefield' && o.blocking) out.push(id);
    }
  }
  return out;
}

/**
 * Deal one damage sub-step. Damage inside a step is simultaneous: every
 * instance is computed first, then applied.
 */
export function dealCombatDamage(ctx: Ctx, step: 'frontrun' | 'regular'): void {
  const state = ctx.state;
  const attackingPlayer = state.activePlayer;
  const defendingPlayer = opponentOf(state, attackingPlayer);
  const instances: DamageInstance[] = [];

  for (const attackerId of state.combat.attackers) {
    const attacker = state.objects[attackerId];
    if (!attacker || attacker.zone !== 'battlefield') continue;
    if (!participatesInStep(state, attackerId, step)) continue;
    if (step === 'regular' && state.combat.dealtFrontrun.includes(attackerId)) continue;

    const assigned = state.combat.blockers[attackerId] ?? [];
    const wasBlocked = assigned.length > 0;
    const liveBlockers = assigned.filter((id) => {
      const b = state.objects[id];
      return b && b.zone === 'battlefield' && b.blocking === attackerId;
    });
    const total = damageOutput(state, attackerId);
    const stats = getEffectiveStats(state, attackerId);

    if (!wasBlocked) {
      if (total > 0) {
        instances.push({ source: attackerId, targetPlayer: defendingPlayer, amount: total });
      }
      if (step === 'frontrun') state.combat.dealtFrontrun.push(attackerId);
      continue;
    }

    if (liveBlockers.length === 0) {
      // All blockers left combat: nothing, unless SLIPPAGE sends it through.
      if (stats.keywords.includes('slippage') && total > 0) {
        instances.push({ source: attackerId, targetPlayer: defendingPlayer, amount: total });
      }
      if (step === 'frontrun') state.combat.dealtFrontrun.push(attackerId);
      continue;
    }

    let left = total;
    const drainer = stats.keywords.includes('drainerLink');
    for (const blockerId of liveBlockers) {
      if (left <= 0) break;
      const bStats = getEffectiveStats(state, blockerId);
      const b = state.objects[blockerId];
      if (!b) continue;
      const lethal = drainer ? 1 : Math.max(1, bStats.health - b.damage);
      const give = Math.min(left, lethal);
      instances.push({ source: attackerId, targetObject: blockerId, amount: give });
      left -= give;
    }
    if (left > 0 && stats.keywords.includes('slippage')) {
      instances.push({ source: attackerId, targetPlayer: defendingPlayer, amount: left });
    }
    if (step === 'frontrun') state.combat.dealtFrontrun.push(attackerId);
  }

  // Blockers strike the attacker they block.
  for (const [attackerId, list] of Object.entries(state.combat.blockers)) {
    const attacker = state.objects[attackerId];
    for (const blockerId of list) {
      const b = state.objects[blockerId];
      if (!b || b.zone !== 'battlefield' || b.blocking !== attackerId) continue;
      if (!participatesInStep(state, blockerId, step)) continue;
      if (step === 'regular' && state.combat.dealtFrontrunBlockers.includes(blockerId)) continue;
      if (step === 'frontrun') state.combat.dealtFrontrunBlockers.push(blockerId);
      if (!attacker || attacker.zone !== 'battlefield') continue;
      const amount = damageOutput(state, blockerId);
      if (amount > 0) instances.push({ source: blockerId, targetObject: attackerId, amount });
    }
  }

  const damagedIds: string[] = [];
  for (const inst of instances) {
    if (inst.targetPlayer) {
      damagePlayer(ctx, inst.source, inst.targetPlayer, inst.amount);
    } else if (inst.targetObject) {
      dealDamage(ctx, inst.source, inst.targetObject, inst.amount);
      damagedIds.push(inst.targetObject);
    }
  }

  runStateBasedChecks(ctx);

  // RULES 6.5: survivors of the step get self:survivesDamage.
  for (const id of [...new Set(damagedIds)]) {
    const o = state.objects[id];
    if (o && o.zone === 'battlefield' && o.damage > 0) {
      fireTriggers(ctx, 'self:survivesDamage', { sourceInstanceId: id });
    }
  }
}

/** Clear combat flags. Damage stays marked until the End step. */
export function endCombat(ctx: Ctx): void {
  const state = ctx.state;
  for (const id of Object.keys(state.objects)) {
    const o = state.objects[id];
    if (o.attacking) o.attacking = undefined;
    if (o.blocking) o.blocking = undefined;
  }
  state.combat.attackers = [];
  state.combat.blockers = {};
  state.combat.dealtFrontrun = [];
  state.combat.dealtFrontrunBlockers = [];
}
